import type { ReactNode } from 'react';
import { brandStyle } from '../../lib/branding';
import { t } from '../../lib/i18n';
import { Link } from 'react-router';
import { Button } from '../../components/ui';
import { LanguagePicker, VisitorLanguageProvider, useVisitorLanguage } from '../public-p4/locale';
import { isInvalidLink, problemText, useLinkBranding, useNoReferrer } from './api';
import type { LinkBranding } from './api';

/**
 * Frame of every public link page: no portal shell, no navigation, the tenant's colours and logo, a phone-first column.
 * The page content is only drawn when the link works; a bad link shows one friendly message whatever the reason.
 */
export function PublicShell({ token = '', slug, title, hideYourData, children }: { token?: string; slug?: string; title?: string; hideYourData?: boolean; children: ReactNode }) {
  const branding = useLinkBranding(token, slug).data;
  return (
    <VisitorLanguageProvider scope={branding?.tenantSlug ?? slug ?? 'default'} languages={branding?.languages}>
      <ShellFrame branding={branding} title={title} hideYourData={hideYourData}>
        {children}
      </ShellFrame>
    </VisitorLanguageProvider>
  );
}

function ShellFrame({ branding, title, hideYourData, children }: { branding: LinkBranding | undefined; title: string | undefined; hideYourData: boolean | undefined; children: ReactNode }) {
  useNoReferrer();
  const { vt } = useVisitorLanguage();
  const style = brandStyle({ primaryColour: branding?.primaryColour ?? '', accentColour: branding?.accentColour ?? '' });
  return (
    <div style={style} className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt={t('publicLinks.logoAlt', { name: branding.displayName })} referrerPolicy="no-referrer" className="h-9 w-auto max-w-[8rem] object-contain" />
          ) : null}
          <span className="truncate text-base font-semibold">{branding?.displayName ?? ''}</span>
          <LanguagePicker />
        </div>
      </header>
      <main className="mx-auto max-w-xl space-y-4 px-4 py-6">
        {title ? <h1 className="text-2xl font-semibold">{title}</h1> : null}
        {children}
      </main>
      <footer className="mx-auto flex max-w-xl flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-8 text-xs text-slate-600">
        <span>{t('publicLinks.footer')}</span>
        {branding?.tenantSlug && !hideYourData ? (
          <Link to={`/${branding.tenantSlug}/privacy`} className="font-medium text-slate-800 underline">
            {vt('public.yourData')}
          </Link>
        ) : null}
      </footer>
    </div>
  );
}

export function LinkProblem({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const invalid = isInvalidLink(error);
  return (
    <div role="alert" className="rounded-lg border border-slate-200 bg-white p-5">
      <h1 className="text-xl font-semibold">{invalid ? t('publicLinks.invalid.title') : t('publicLinks.problem.title')}</h1>
      <p className="mt-2 text-slate-700">{invalid ? t('publicLinks.invalid.body') : problemText(error)}</p>
      {!invalid && onRetry ? (
        <Button className="mt-4" onClick={onRetry}>
          {t('publicLinks.retry')}
        </Button>
      ) : null}
    </div>
  );
}

export function PageLoading() {
  return (
    <div role="status" aria-busy="true" className="space-y-3">
      <span className="sr-only">{t('publicLinks.loading')}</span>
      <div className="cs-skeleton h-8 w-2/3" />
      <div className="cs-skeleton h-32 w-full" />
    </div>
  );
}

/** A white card section used by all the public pages. */
export function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      {title ? <h2 className="mb-3 text-base font-semibold">{title}</h2> : null}
      <div className="space-y-3">{children}</div>
    </section>
  );
}
